<script setup>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import GmAnalyticsDashboard from '../components/GmAnalyticsDashboard.vue';

const store = useStore();
const { t } = useI18n();

// Reactive data
const isLoading = ref(true);
const error = ref(null);
const analyticsData = ref({});

// Computed properties
const hasData = computed(() => {
  return Object.keys(analyticsData.value).length > 0;
});

// Methods
const handleChartRefresh = async (event) => {
  console.log('🔄 GM Analytics Dashboard: Chart refresh requested:', event);
  const { chartType, filters } = event;
  
  // Update store filters
  if (filters) {
    await store.dispatch('gmAnalytics/updateFilters', filters);
  }
  
  // Refresh dashboard data with new filters
  await fetchAnalyticsData();
};

const handleChartExport = (event) => {
  console.log('📊 GM Analytics Dashboard: Chart export requested:', event);
  const { chartType, filters } = event;
  
  // TODO: Implement export functionality
  console.log(`Exporting ${chartType} with filters:`, filters);
};

const handleFiltersChange = async (event) => {
  console.log('🔧 GM Analytics Dashboard PAGE: Filters changed event received:', event);
  console.log('🔧 GM Analytics Dashboard PAGE: Event type:', typeof event);
  console.log('🔧 GM Analytics Dashboard PAGE: Event keys:', Object.keys(event));
  
  const { chartType, filters } = event;
  const actualFilters = filters.filters; // Extract the real filters object
  console.log('🔧 GM Analytics Dashboard PAGE: Extracted chartType:', chartType);
  console.log('🔧 GM Analytics Dashboard PAGE: Extracted filters:', filters);
  console.log('🔧 GM Analytics Dashboard PAGE: Extracted actualFilters:', actualFilters);
  console.log('🔧 GM Analytics Dashboard PAGE: ActualFilters type:', typeof actualFilters);
  console.log('🔧 GM Analytics Dashboard PAGE: ActualFilters keys:', Object.keys(actualFilters || {}));
  console.log('🔧 GM Analytics Dashboard PAGE: Date range in actualFilters:', actualFilters?.dateRange);
  console.log('🔧 GM Analytics Dashboard PAGE: Preset value:', actualFilters?.dateRange?.preset);
  
  // Update store filters
  if (actualFilters) {
    console.log('🔧 GM Analytics Dashboard PAGE: Updating store with actualFilters:', actualFilters);
    await store.dispatch('gmAnalytics/updateFilters', actualFilters);
  }
  
  // Don't refresh the whole dashboard - let each chart handle its own data
  console.log('🔧 GM Analytics Dashboard PAGE: Chart filter updated, not refreshing whole dashboard');
};

const fetchAnalyticsData = async () => {
  try {
    console.log('🔄 GM Analytics Dashboard: Fetching analytics data...');
    isLoading.value = true;
    error.value = null;

    // Fetch all analytics data
    console.log('🔄 GM Analytics Dashboard: About to dispatch store actions...');
    
    try {
      console.log('🔄 GM Analytics Dashboard: Dispatching fetchDashboardData...');
      await store.dispatch('gmAnalytics/fetchDashboardData');
      console.log('✅ GM Analytics Dashboard: fetchDashboardData completed');
    } catch (error) {
      console.error('❌ GM Analytics Dashboard: Error in store dispatch:', error);
      throw error;
    }
    
    console.log('✅ GM Analytics Dashboard: All analytics data fetched successfully');
    
        // Get data from store state
        console.log('🔍 GM Analytics Dashboard: Store state before reading:', store.state.gmAnalytics);
        console.log('🔍 GM Analytics Dashboard: Store getters before reading:', {
          dashboardData: store.getters['gmAnalytics/getDashboardData']
        });
        
        analyticsData.value = store.getters['gmAnalytics/getDashboardData'] || {};

        // Debug: Log what data we actually received
        console.log('🔍 GM Analytics Dashboard: Analytics data received:', analyticsData.value);
        console.log('🔍 GM Analytics Dashboard: Analytics data keys:', Object.keys(analyticsData.value));
        console.log('🔍 GM Analytics Dashboard: Has data check:', Object.keys(analyticsData.value).length > 0);
    
  } catch (err) {
    console.error('❌ GM Analytics Dashboard: Error fetching analytics data:', err);
    error.value = err.message || 'Failed to fetch analytics data';
  } finally {
    isLoading.value = false;
    console.log('🏁 GM Analytics Dashboard: Data fetching completed');
  }
};


// Lifecycle
onMounted(async () => {
  console.log('🎯 GM Analytics Dashboard: Component mounted, loading store and starting data fetch...');
  
      // Dynamically load the GM Analytics store
      try {
        console.log('🔄 GM Analytics Dashboard: Loading store module...');
        const gmAnalyticsStore = await import('dashboard/store/gm-analytics');
        console.log('✅ GM Analytics Dashboard: Store module imported:', gmAnalyticsStore);
        
        store.registerModule('gmAnalytics', gmAnalyticsStore.default);
        console.log('✅ GM Analytics Dashboard: Store registered successfully');
        
        // Check if store is accessible
        console.log('🔍 GM Analytics Dashboard: Store state check:', store.state.gmAnalytics);
        console.log('🔍 GM Analytics Dashboard: Store getters check:', store.getters['gmAnalytics/getDashboardData']);
        console.log('🔍 GM Analytics Dashboard: All store modules:', Object.keys(store.state));
        console.log('🔍 GM Analytics Dashboard: All store getters:', Object.keys(store.getters));
        console.log('🔍 GM Analytics Dashboard: GM Analytics getters:', Object.keys(store.getters).filter(key => key.startsWith('gmAnalytics/')));
      } catch (error) {
        console.error('❌ GM Analytics Dashboard: Error loading store:', error);
        console.error('❌ GM Analytics Dashboard: Error details:', error.message, error.stack);
      }
  
  fetchAnalyticsData();
});
</script>

<template>
  <div class="gm-analytics-dashboard">

    <!-- Error State -->
    <div v-if="error" class="gm-analytics-error">
      <div class="gm-error-content">
        <i class="icon-warning"></i>
        <h3>{{ t('GM_ANALYTICS.DASHBOARD.ERROR_TITLE', 'Error Loading Analytics') }}</h3>
        <p>{{ error }}</p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else-if="isLoading" class="gm-analytics-loading">
      <div class="gm-loading-content">
        <div class="gm-loading-spinner"></div>
        <h3>{{ t('GM_ANALYTICS.DASHBOARD.LOADING_TITLE', 'Loading Analytics...') }}</h3>
        <p>{{ t('GM_ANALYTICS.DASHBOARD.LOADING_SUBTITLE', 'Preparing your world-class analytics dashboard') }}</p>
      </div>
    </div>

    <!-- Main Dashboard Content -->
    <div v-else-if="hasData" class="gm-analytics-content">
      <GmAnalyticsDashboard 
        :data="analyticsData" 
        :filters="store.getters['gmAnalytics/getFilters']"
        @chart-refresh="handleChartRefresh"
        @chart-export="handleChartExport"
        @filters-change="handleFiltersChange"
      />
    </div>

    <!-- Empty State -->
    <div v-else class="gm-analytics-empty">
      <div class="gm-empty-content">
        <i class="icon-analytics-empty"></i>
        <h3>{{ t('GM_ANALYTICS.DASHBOARD.EMPTY_TITLE', 'No Analytics Data Available') }}</h3>
        <p>{{ t('GM_ANALYTICS.DASHBOARD.EMPTY_SUBTITLE', 'Start collecting data to see your analytics dashboard') }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gm-analytics-dashboard {
  padding: 1.5rem;
  background: #f8fafc;
  min-height: 100vh;
}


.gm-analytics-content {
  background: transparent;
}

.gm-analytics-error,
.gm-analytics-loading,
.gm-analytics-empty {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #f1f5f9;
}

.gm-error-content,
.gm-loading-content,
.gm-empty-content {
  text-align: center;
  max-width: 400px;
}

.gm-loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}


@media (max-width: 768px) {
  .gm-analytics-dashboard {
    padding: 1rem;
  }
}
</style>
